import React, { FC, useState } from 'react';
import { TranslationsContainerProps } from './TranslationsContainer.types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  BorderLinearProgress,
  ContainerContent,
  DashboardContainerHeader,
  ContainerScroll, ContainerTranslationItem, DashboardContainer, EventsAmount, EventsSubtitle,
  H2,
  H3,
  StyledTitle
} from '../MoaDashboard.styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { TranslationCountContainer } from './TranslationContainer.styles';
import SideDrawer from '../../../components/Drawer/Drawer';
import {
    useCouponTranslationFailures, useEventTranslationFailures, useTranslationFailures
} from '../../../services/dashboard/dashboard.service';
import { Translation } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams, ValueFormatterParams } from 'ag-grid-community';

const TranslationsContainer: FC<TranslationsContainerProps> = ({
                                                                 menuTranslation,
                                                                 promotionTranslation,
                                                                 eventTranslation,
                                                                 from,
                                                                 to,
                                                                 isLoading
                                                               }) => {
  const [openMenuDrawer, setOpenMenuDrawer] = useState(false);
  const [openCouponDrawer, setOpenCouponDrawer] = useState(false);
  const [openEventTranslationDrawer, setOpenEventTranslationDrawer] = useState(false);
  const muiTheme = createTheme();

  const { data: menuTranslationFailures } = useTranslationFailures({
    from: from?.toISOString(),
    to: to?.get('minutes') === 55 ? to?.set('minutes', 59).toISOString() : to?.toISOString(),
  }, openMenuDrawer);
  const availableMenuTranslationFailures = menuTranslationFailures?.data || [];

  const { data: couponTranslationFailures } = useCouponTranslationFailures({
    from: from?.toISOString(),
    to: to?.get('minutes') === 55 ? to?.set('minutes', 59).toISOString() : to?.toISOString(),
  }, openCouponDrawer);
  const availableCouponTranslationFailures = couponTranslationFailures?.data || [];

  const { data: eventTranslationFailures } = useEventTranslationFailures(
      {
          failed: true,
          from: from?.toISOString(),
          to: to?.get('minutes') === 55 ? to?.set('minutes', 59).toISOString() : to?.toISOString(),
      },
      openEventTranslationDrawer
  );

  const hasNoTranslations: boolean = menuTranslation.total === 0 && promotionTranslation.total === 0;

  if (hasNoTranslations || isLoading) {
    return (
        <DashboardContainer>
          <DashboardContainerHeader>Translations</DashboardContainerHeader>
          <ContainerScroll data-testid="translationsEmptyContainer"
                           style={{ height: '100px', alignItems: 'baseline' }}>
            <StyledTitle>No translations at this moment</StyledTitle>
          </ContainerScroll>
        </DashboardContainer>
    );
  }

  const toggleDrawer = (newOpen: boolean) => {
    setOpenMenuDrawer(newOpen);
  };

  const defineDrawer = () => {
    setOpenMenuDrawer(true);
  };

  const toggleCouponDrawer = (newOpen: boolean) => {
    setOpenCouponDrawer(newOpen);
  };

  const defineCouponDrawer = () => {
    setOpenCouponDrawer(true);
  };

  const translationsFailuresColumnDefs = [
    {
      minWidth: 120,
      field: 'time',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
    },
    {
      minWidth: 80,
      field: 'marketNo',
      headerName: 'Market',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      minWidth: 80,
      field: 'storeNo',
      headerName: 'Store',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
  ];

  const couponsTranslationsFailuresColumnDefs = [
    {
      minWidth: 80,
      field: 'time',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
    },
    {
      minWidth: 80,
      field: 'promotionName',
      headerName: 'Promotion',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
  ];

  const eventTranslationFailuresColumnDefs = [
      {
          minWidth: 80,
          field: 'time',
          headerName: 'Time',
          sortable: true,
          floatingFilter: false,
          valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
          tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value)).toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString()),
      },
      {
          minWidth: 80,
          field: 'eventType',
          headerName: 'Event',
          sortable: true,
          floatingFilter: false,
          tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
      {
          minWidth: 80,
          field: 'objectName',
          headerName: 'Name',
          sortable: true,
          floatingFilter: false,
          tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
      {
          minWidth: 300,
          field: 'errorReason',
          headerName: 'Error Reason',
          sortable: true,
          floatingFilter: false,
          tooltipValueGetter: (params: ITooltipParams) => params.value,
      },
  ];

    const createDateAsUTC = (date: Date) => {
      return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

  const renderMenuTranslation = (translationItem: Translation) => {
    if (translationItem === null || translationItem.total === 0) {
      return null;
    }
    if (translationItem.total === translationItem.processed) {
      return (
        <ContainerScroll data-testid="translationsAllDoneContainer">
          <ContainerContent style={{ paddingTop: '9px' }}>
            <TranslationCountContainer>
              <EventsAmount data-testid="translationsCounter">
                {translationItem.total}
              </EventsAmount>
              <EventsSubtitle>
                Menus translated
              </EventsSubtitle>
            </TranslationCountContainer>
            {translationItem.failed > 0 &&
              <>
                <ErrorOutlineIcon
                  data-testid="failedDoneTranslationsLink"
                  fontSize="medium" aria-label="Failed" style={{
                  color: 'red',
                  marginLeft: 'auto',
                  alignContent: 'flex-end',
                  paddingRight: '5px',
                  paddingTop: '6px',
                }} />
                <H3 dataTestId="failedProcessingLink"
                    label={translationItem.failed + ' failed'} to={''}
                    onClick={() => defineDrawer()}
                    style={{ paddingLeft: '0' }} />
                <ThemeProvider theme={muiTheme}
                               key={'time-picker-mui-theme'}>
                  <SideDrawer open={openMenuDrawer}
                              from={from}
                              toggleDrawer={toggleDrawer}
                              title={'Translation Menu Failures'}
                              columnDefs={translationsFailuresColumnDefs}
                              rowData={availableMenuTranslationFailures} />
                </ThemeProvider>
              </>
            }
          </ContainerContent>
        </ContainerScroll>
      );
    }
    return (
      <ContainerScroll data-testid="translationsProcessingContainer">
        <StyledTitle>Processing Menus</StyledTitle>
        <ContainerContent style={{ position: 'relative' }}>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <H2 style={{
              paddingTop: '4px',
              paddingLeft: '5px',
              position: 'absolute',
              left: '15px',
              zIndex: 10,
              fontWeight: 600,
            }}>{translationItem.processed}</H2>
            <BorderLinearProgress data-testid="translationsProgressBar"
                                  variant="determinate"
                                  value={translationItem.processed / translationItem.total * 100}
                                  style={{ width: '60%' }} />
          </ThemeProvider>
          <H2 style={{
            paddingTop: '4px',
            paddingLeft: '5px',
            fontWeight: 600,
          }}>{translationItem.total}</H2>
          {translationItem.failed > 0 &&
            <>
              <ErrorOutlineIcon data-testid="failedProcessingIcon"
                                fontSize="medium" aria-label="Failed" style={{
                color: 'red',
                marginLeft: 'auto',
                alignContent: 'flex-end',
                paddingRight: '5px',
                paddingTop: '6px',
              }} />
              <H3 dataTestId="failedProcessingLink"
                  label={translationItem.failed + ' failed'} to={''}
                  onClick={() => defineDrawer()}
                  style={{ paddingLeft: '0' }} />
              <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
                <SideDrawer open={openMenuDrawer}
                            toggleDrawer={toggleDrawer}
                            title={'Translation Menu Failures'}
                            columnDefs={translationsFailuresColumnDefs}
                            rowData={availableMenuTranslationFailures} />
              </ThemeProvider>
            </>
          }
        </ContainerContent>
      </ContainerScroll>
    );
  };

  const renderPromotionTranslation = (translationItem: Translation) => {
    if (translationItem === null || translationItem.total === 0) {
      return null;
    }
    if (translationItem.total === translationItem.processed) {
      return (
        <ContainerScroll data-testid="translationsPromotionAllDoneContainer">
          <ContainerContent style={{ paddingTop: '9px' }}>
            <TranslationCountContainer>
              <EventsAmount data-testid="promotionsCounter">
                {translationItem.total}
              </EventsAmount>
              <EventsSubtitle>
                Coupons translated
              </EventsSubtitle>
            </TranslationCountContainer>
            {translationItem.failed > 0 ?
              <>
                <ErrorOutlineIcon
                  data-testid="failedDonePromotionsTranslationsLink"
                  fontSize="medium" aria-label="Failed" style={{
                  color: 'red',
                  marginLeft: 'auto',
                  alignContent: 'flex-end',
                  paddingRight: '5px',
                  paddingTop: '6px',
                }} />
                <H3 dataTestId="failedCouponProcessingLink"
                  label={translationItem.failed + ' failed'} to={''}
                    onClick={() => defineCouponDrawer()}
                    style={{ paddingLeft: '0' }} />
                <ThemeProvider theme={muiTheme}
                               key={'time-picker-mui-theme'}>
                  <SideDrawer open={openCouponDrawer}
                              toggleDrawer={toggleCouponDrawer}
                              title={'Translation Coupon Failures'}
                              columnDefs={couponsTranslationsFailuresColumnDefs}
                              rowData={availableCouponTranslationFailures} />
                </ThemeProvider>
              </>
              :
              null
            }
          </ContainerContent>
        </ContainerScroll>
      );
    }
    return (
      <ContainerScroll data-testid="translationsCouponProcessingContainer">
        <StyledTitle>Processing Coupons</StyledTitle>
        <ContainerContent style={{ position: 'relative' }}>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <H2 style={{
              paddingTop: '4px',
              paddingLeft: '5px',
              position: 'absolute',
              left: '15px',
              zIndex: 10,
              fontWeight: 600,
            }}>{translationItem.processed}</H2>
            <BorderLinearProgress data-testid="translationsCouponProgressBar"
                                  variant="determinate"
                                  value={translationItem.processed / translationItem.total * 100}
                                  style={{ width: '60%' }} />
          </ThemeProvider>
          <H2 style={{
            paddingTop: '4px',
            paddingLeft: '5px',
            fontWeight: 600,
          }}>{translationItem.total}</H2>
          {translationItem.failed > 0 ?
            <>
              <ErrorOutlineIcon data-testid="failedCouponProcessingIcon"
                                fontSize="medium" aria-label="Failed" style={{
                color: 'red',
                marginLeft: 'auto',
                alignContent: 'flex-end',
                paddingRight: '5px',
                paddingTop: '6px',
              }} />
              <H3 dataTestId="failedCouponProcessingLink"
                  label={translationItem.failed + ' failed'} to={''}
                  onClick={() => defineCouponDrawer()}
                  style={{ paddingLeft: '0' }} />
              <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
                <SideDrawer open={openCouponDrawer}
                            toggleDrawer={toggleCouponDrawer}
                            title={'Translation Coupons Failures'}
                            columnDefs={couponsTranslationsFailuresColumnDefs}
                            rowData={availableCouponTranslationFailures} />
              </ThemeProvider>
            </>
            :
            null
          }
        </ContainerContent>
      </ContainerScroll>
    );
  };

  const renderEventTranslation = (translationItem: Translation) => {
      return translationItem.total === translationItem.processed
          ? eventTranslationDoneContainer(translationItem)
          : eventTranslationProcessingContainer(translationItem)
  };

  const eventTranslationDoneContainer = (translationItem: Translation) => {
      return (
          <ContainerScroll data-testid="translationsEventAllDoneContainer">
              <ContainerContent style={{ paddingTop: '9px' }}>
                  <TranslationCountContainer>
                      <EventsAmount data-testid="eventsCounter">
                          {translationItem.total}
                      </EventsAmount>
                      <EventsSubtitle>
                          Events translated
                      </EventsSubtitle>
                  </TranslationCountContainer>
                  {translationItem.failed > 0 &&
                      failedEventTranslation(translationItem)
                  }
              </ContainerContent>
          </ContainerScroll>
      );
  }

  const eventTranslationProcessingContainer = (translationItem: Translation) => {
      return (
          <ContainerScroll data-testid="translationsEventProcessingContainer">
              <StyledTitle>Processing Events</StyledTitle>
              <ContainerContent style={{ position: 'relative' }}>
                  <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
                      <H2 style={{
                          paddingTop: '4px',
                          paddingLeft: '5px',
                          position: 'absolute',
                          left: '15px',
                          zIndex: 10,
                          fontWeight: 600
                      }}>
                          {translationItem.processed}
                      </H2>
                      <BorderLinearProgress
                          data-testid="translationsEventProgressBar"
                          variant="determinate"
                          value={translationItem.processed / translationItem.total * 100}
                          style={{ width: '60%' }}
                      />
                  </ThemeProvider>
                  <H2 style={{
                      paddingTop: '4px',
                      paddingLeft: '5px',
                      fontWeight: 600
                  }}>
                      {translationItem.total}
                  </H2>
                  {translationItem.failed > 0 &&
                      failedEventTranslation(translationItem)
                  }
              </ContainerContent>
          </ContainerScroll>
      );
  }

  const failedEventTranslation = (translationItem: Translation) => {
    return (
        <>
          <ErrorOutlineIcon
              data-testid="failedEventsTranslationsLink"
              fontSize="medium"
              aria-label="Failed"
              style={{
                  color: 'red',
                  marginLeft: 'auto',
                  alignContent: 'flex-end',
                  paddingRight: '5px',
                  paddingTop: '6px'
              }}
          />
          <H3
              dataTestId="failedEventTranslationsProcessingLink"
              label={`${translationItem.failed} failed`} to={''}
              onClick={() => setOpenEventTranslationDrawer(true)}
              style={{ paddingLeft: '0' }}
          />
          <ThemeProvider theme={muiTheme}
                         key={'time-picker-mui-theme'}>
            <SideDrawer open={openEventTranslationDrawer}
                        toggleDrawer={setOpenEventTranslationDrawer}
                        title={'Translation Failures'}
                        columnDefs={eventTranslationFailuresColumnDefs}
                        rowData={eventTranslationFailures?.data || []}/>
          </ThemeProvider>
        </>
    )
  }

  return (
      <DashboardContainer>
        <DashboardContainerHeader>Translations</DashboardContainerHeader>
        <ContainerTranslationItem>
          {renderMenuTranslation(menuTranslation)}
        </ContainerTranslationItem>
        <ContainerTranslationItem>
          {renderPromotionTranslation(promotionTranslation)}
        </ContainerTranslationItem>
        <ContainerTranslationItem>
          {eventTranslation && eventTranslation.total !== 0
              ? renderEventTranslation(eventTranslation)
              : null
          }
        </ContainerTranslationItem>
      </DashboardContainer>
  );
};

export default TranslationsContainer;